import styled from "styled-components";
import { getSimpleColorSpan } from "../../../utils/CardUtils";
import { formatDefaultDateTime, formatPrice } from "../../../utils/Formatter";
import { ButtonsEnabledRules, getOrderSpanWithColor } from "../../pages/orders/OrdersPage";
import OrderMissingSizesTooltip, {
    MISSING_SIZES_STATUS,
    getMissingSizeTooltip,
    isMissingSizeStatus
} from "../../pages/orders/ordersComponents/OrderMissingSizesTooltip";
import GridCardComponent from "./generic/GridCardComponent";

// #region  SCOPED CSS
const StyledPage = styled.div`
    .Image-5 {
        padding: 15px !important;
    }
    .Frame-161 {
        margin-bottom: 4px !important;
    }

    .missing-sizes-status {
        align-items: center;
        display: inline-flex;
        gap: 4px;
        line-height: 14px;
        white-space: nowrap;
    }

    .missing-sizes-status svg {
        margin-right: 0;
        top: 0;
    }
`;
// #endregion

/**
 * Renders a grid card component for displaying order information.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {string} [props.id] - The ID of the grid card.
 * @param {string} [props.posLogo] - The URL for the POS logo.
 * @param {string} [props.image] - The image URL for the card.
 * @param {boolean} [props.isCardEnabled] - Indicates whether the card is enabled.
 * @param {Object} props.data - The order data object.
 * @param {string} [props.data.status] - The status of the order.
 * @param {string} [props.data.styleCode] - The style code of the order.
 * @param {string} [props.data.fromBrand] - The brand the order is from.
 * @param {string} [props.data.orderToBrand] - The brand the order is to.
 * @param {string} [props.data.date] - The date of the order.
 * @param {number|string} [props.data.totalItems] - The total number of items in the order.
 * @param {string} [props.data.importedFrom] - The source from which the order was imported.
 * @param {boolean} [props.isArchivePage] - Indicates whether the card is for archived orders page.
 * @returns {JSX.Element} The rendered OrdersGridCard component.
 */
export default function OrdersGridCard(props) {
    // #region METHODS
    /*const getHighlightColor = status => {
        if (status?.toLowerCase() === "ready to export") {
            return { show: false, background_color: "var(--text-icons-green-500)", highlightText: "Ready to export" };
        }
        if (status?.toLowerCase() === "failed to export") {
            return { show: false, background_color: "var(--text-icons-red-500)", highlightText: "Failed to export" };
        }
        if (status?.toLowerCase() === "exported") {
            return { show: false, background_color: "var(--text-icons-blue-500)", highlightText: "Exported" };
        }
        return { show: false, background_color: undefined, highlightText: undefined };
    };*/
    //const { show: showHighlight, background_color, highlightText } = getHighlightColor(mappedStatus);

    const getStatusView = data => {
        if (!isMissingSizeStatus(data?.status)) return getOrderSpanWithColor(data?.status, props?.isArchivePage);

        return (
            <span className="missing-sizes-status span-with-color text-orange">
                {MISSING_SIZES_STATUS}
                <OrderMissingSizesTooltip data={data} iconSize="14px" placement="left" />
            </span>
        );
    };
    // #endregion

    // #region VARS
    const status = props?.data?.status || "--";

    const seasonTxt = Array.isArray(props?.data?.seasons) ? props?.data?.seasons?.join(", ") : "--";
    // #endregion

    // #region RENDER
    return (
        <StyledPage className={`${props?.className || ""}`}>
            <GridCardComponent
                //status={mappedStatus}
                key={props?.id}
                posLogo={props?.posLogo}
                title={props?.data?.orderNumber || "--"}
                titlePrefix={"Order NR:"}
                image={props?.data?.labelBrandImageURL || ""}
                imageBorderRadius={"0px"}
                imageFit="contain"
                imageHasPadding={false}
                imageHeight={"69px"}
                id={props?.id || "--"}
                isCardEnabled={props?.isCardEnabled}
                isCardSuspended={false}
                details={[
                    { key: "From brand:", value: props?.data?.labelBrandName || "--" },
                    { key: "To store:", value: props?.data?.storeName || "--" },
                    { key: "Date:", value: formatDefaultDateTime(props?.data?.createdAt) },
                    //{ key: "Total:", value: formatPrice(props.data?.totalPriceIncludingVAT, "kr", "--") },
                    { key: "Imported from:", value: props?.isCardEnabled ? "Email" : "--" }, //props.data?.importedFromPosName || "--" },
                    {
                        key: "Season:",
                        value: !seasonTxt || !props?.isCardEnabled ? "--" : getSimpleColorSpan(seasonTxt, "green")
                    },
                    {
                        key: "Total items:",
                        value: !props?.data?.totalItems ? "--" : getSimpleColorSpan(props?.data?.totalItems, "green")
                    },
                    {
                        key: "Total:",
                        value: !props?.data?.totalPriceIncludingVAT
                            ? "--"
                            : getSimpleColorSpan(formatPrice(props.data?.totalPriceIncludingVAT, "kr", "--"), "green")
                    },
                    {
                        key: "Status:",
                        value: getStatusView(props?.data),
                        hideTooltip: isMissingSizeStatus(status)
                    }
                ]}
                edit={{ hidden: true }}
                suspend={{ hidden: true }}
                delete={{ hidden: true }}
                cardData={props?.cardData}
                noOfRows={8}
                //highlighted={showHighlight}
                //highlightColor={background_color}
                //highlightCustomText={highlightText}
                showNewLabel={false}
                showSelections={true}
                thirdTopButton={{
                    title: "DELETE",
                    onClick: props?.onDeleteClick ? item => props?.onDeleteClick(item) : undefined
                }}
                fourthTopButton={{
                    title: "VIEW",
                    onClick: props?.onViewClick ? item => props?.onViewClick(item) : undefined
                }}
                secondaryButton={{
                    title: "EXPORT",
                    onClick: props?.onExportClick ? item => props?.onExportClick(item) : undefined,
                    disabled: !ButtonsEnabledRules("export", status)
                }}
                primaryButton={{
                    title: "RULES",
                    onClick: props?.onRulesClick ? item => props?.onRulesClick(item) : undefined,
                    disabled: !ButtonsEnabledRules("rules", status)
                }}
            />
        </StyledPage>
    );
    // #endregion
}

// #region EXPORTED COMPONENTS
export { getMissingSizeTooltip };
// #endregion
